/**
 * Pure helpers for saved customer cards (OpenSpec add-saved-customer-credit-cards).
 *
 * BUNDLED INTO THE CLIENT. Keep this module free of anything that only resolves
 * on the server — db, logger, config, models, `*.pg.ts`. The expiry rule lives
 * here precisely so the receipt form greys a card out on the SAME rule the
 * charge endpoint refuses it on.
 */

/**
 * The shape of a saved card as it reaches the browser. There is deliberately no
 * `token` here: the server's SELECTs never project it, and a type that cannot
 * hold it keeps a future caller from adding it by accident.
 */
export interface SavedCustomerCard {
    _id: number;
    passengerId: number;
    last4: string | null;
    brand: string | null;
    expMonth: number | null;
    expYear: number | null;
    cardHolderName: string | null;
    /** Naive Israel wall clock, 'YYYY-MM-DD HH:mm:ss' — display with {@link formatWallClock}. */
    createdAt: string | null;
    createdByName: string | null;
    /** Naive Israel wall clock, 'YYYY-MM-DD HH:mm:ss'. */
    lastUsedAt: string | null;
    /** Computed server-side on the Israel date, with {@link isCardExpired}. */
    isExpired: boolean;
    /** Only on the travel-file listing, to label whose card it is. */
    passengerName?: string | null;
}

/** A passenger that a card can be saved for, on the travel-file listing. */
export interface CardOwnerPassenger {
    passengerId: number;
    name: string;
}

export interface TravelFileSavedCards {
    passengers: CardOwnerPassenger[];
    cards: SavedCustomerCard[];
    /**
     * The travel file's start date, local wall clock 'YYYY-MM-DDTHH:mm:ss' (or
     * null) — the instalment rules key off it, on the form and on the server.
     */
    tripDate: string | null;
    /**
     * The tenant's `fullPaymentWindowDays` (tourregistrationsettings): no split
     * inside this many days of the trip. Resolved server-side exactly as the
     * charge endpoint and the payment page resolve it.
     */
    fullPaymentWindowDays: number;
    /** Booked through a travel agent: the agent pays, so no saved passenger card may. */
    bookedThroughAgent: boolean;
}

/**
 * The charge dialog is built so CVV and the holder's ID number can be asked for
 * on a token charge WITHOUT rework, once the real-terminal test (tasks 0.1–0.2)
 * says Pelecard needs them (codes 058 / 059). Off until then.
 *
 * When on, the values travel browser → server → Pelecard in one request and are
 * never stored or logged.
 */
export const SAVED_CARD_CHARGE_ASKS_CVV_AND_HOLDER_ID = false;

/** Outcome kinds the charge endpoint answers with, mirrored by the receipt forms. */
export type SavedCardChargeStatus = 'SUCCESS' | 'FAILED' | 'UNKNOWN';

export interface CardExpiry {
    expMonth: number;
    expYear: number;
}

/**
 * Pelecard's `CreditCardExpDate` → month and four-digit year.
 *
 * Pelecard sends MMYY ("1230"). "MM/YY" and "MM/YYYY" are accepted too, since the
 * separator is cosmetic. Anything else returns null rather than a guess — a card
 * whose expiry we cannot read is a card whose expiry we cannot enforce.
 */
export const parseCardExpiry = (raw: unknown): CardExpiry | null => {
    if (raw === null || raw === undefined) return null;
    const digits = String(raw).replace(/\D/g, '');
    let month: number;
    let year: number;
    if (digits.length === 4) {
        month = Number(digits.slice(0, 2));
        year = 2000 + Number(digits.slice(2));
    } else if (digits.length === 6) {
        month = Number(digits.slice(0, 2));
        year = Number(digits.slice(2));
    } else {
        return null;
    }
    if (!Number.isInteger(month) || month < 1 || month > 12) return null;
    if (!Number.isInteger(year) || year < 2000 || year > 2099) return null;
    return { expMonth: month, expYear: year };
};

/** Today's calendar year and month in Israel, whatever timezone the process runs in. */
export const israelYearMonth = (now: Date = new Date()): { year: number; month: number } => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Jerusalem',
        year: 'numeric',
        month: '2-digit'
    }).formatToParts(now);
    const year = Number(parts.find((p) => p.type === 'year')?.value);
    const month = Number(parts.find((p) => p.type === 'month')?.value);
    return { year, month };
};

/**
 * A card is valid THROUGH the last day of its expiry month, so it is expired
 * only once that whole month has passed — i.e. when (expYear, expMonth) is
 * strictly before the current Israel (year, month).
 *
 * A card with no readable expiry counts as expired: it cannot be shown to be
 * valid, and charging it is the unsafe default.
 */
export const isCardExpired = (
    card: { expMonth?: number | null; expYear?: number | null },
    now: Date = new Date()
): boolean => {
    const expMonth = Number(card?.expMonth);
    const expYear = Number(card?.expYear);
    if (!Number.isInteger(expMonth) || !Number.isInteger(expYear) || expMonth < 1 || expMonth > 12) {
        return true;
    }
    const today = israelYearMonth(now);
    if (expYear !== today.year) return expYear < today.year;
    return expMonth < today.month;
};

/** The last four digits of a masked PAN (`455744******4990`, `XXXXXXXXXXXX4990`). */
export const last4FromMaskedNumber = (raw: unknown): string | null => {
    if (raw === null || raw === undefined) return null;
    const digits = String(raw).replace(/\D/g, '');
    return digits.length >= 4 ? digits.slice(-4) : null;
};

/** "12/30" — the way the card itself prints it. */
export const formatCardExpiry = (card: { expMonth?: number | null; expYear?: number | null }): string => {
    const month = Number(card?.expMonth);
    const year = Number(card?.expYear);
    if (!month || !year) return '--/--';
    return `${String(month).padStart(2, '0')}/${String(year % 100).padStart(2, '0')}`;
};

/** "Visa •••• 4580" — never more than the last four digits. */
export const maskedCardLabel = (card: { brand?: string | null; last4?: string | null }): string => {
    const brand = (card?.brand || '').trim() || 'Card';
    const last4 = (card?.last4 || '').replace(/\D/g, '').slice(-4);
    return last4 ? `${brand} •••• ${last4}` : brand;
};

/** The card networks the card tiles draw a mark for; everything else is `other`. */
export type CardBrandKey = 'visa' | 'mastercard' | 'amex' | 'diners' | 'isracard' | 'other';

/**
 * Pelecard's brand arrives as free text — `CardEnName`, the Hebrew name, or an
 * issuer string, whichever the transaction carried. International networks are
 * matched first, so a co-branded "Isracard Mastercard" shows the network's mark.
 */
const BRAND_PATTERNS: ReadonlyArray<readonly [CardBrandKey, RegExp]> = [
    ['amex', /amex|american\s*express|אמריקן|אמקס/i],
    ['mastercard', /master|מא?סטר/i],
    ['visa', /visa|ויזה/i],
    ['diners', /diners|דיינרס/i],
    ['isracard', /isra|ישראכרט/i]
];

const BRAND_NAMES: Record<Exclude<CardBrandKey, 'other'>, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    diners: 'Diners Club',
    isracard: 'Isracard'
};

export const cardBrandKey = (brand: string | null | undefined): CardBrandKey => {
    const text = String(brand ?? '').trim();
    if (!text) return 'other';
    return BRAND_PATTERNS.find(([, pattern]) => pattern.test(text))?.[0] ?? 'other';
};

/** "Visa", "American Express" — or the brand as Pelecard wrote it when it is none we know. */
export const cardBrandName = (brand: string | null | undefined): string => {
    const key = cardBrandKey(brand);
    return key === 'other' ? String(brand ?? '').trim() || 'Card' : BRAND_NAMES[key];
};

/** "03/2029" — the long form the card lists show. */
export const formatCardExpiryLong = (card: { expMonth?: number | null; expYear?: number | null }): string => {
    const month = Number(card?.expMonth);
    const year = Number(card?.expYear);
    if (!month || !year) return '--/----';
    return `${String(month).padStart(2, '0')}/${year}`;
};

/** "Visa ending in 4580" — never more than the last four digits. */
export const cardEndingLabel = (card: { brand?: string | null; last4?: string | null }): string => {
    const last4 = (card?.last4 || '').replace(/\D/g, '').slice(-4);
    const name = cardBrandName(card?.brand);
    return last4 ? `${name} ending in ${last4}` : name;
};

/**
 * "Visa ending in 4580, Dana Levi, expires 10/2026" — the accessible name of a card
 * option. The brand is spoken here because the list shows it only as a logo.
 */
export const savedCardAccessibleLabel = (card: {
    brand?: string | null;
    last4?: string | null;
    expMonth?: number | null;
    expYear?: number | null;
    isExpired?: boolean;
    cardHolderName?: string | null;
}): string => {
    const holder = (card?.cardHolderName || '').trim();
    return `${cardEndingLabel(card)}${holder ? `, ${holder}` : ''}, ${card?.isExpired ? 'expired' : 'expires'} ${formatCardExpiryLong(card)}`;
};

/**
 * 'YYYY-MM-DD HH:mm:ss' (naive Israel wall clock) → 'DD/MM/YYYY HH:mm', read
 * straight out of the string. Never through `new Date()`, which would treat the
 * wall clock as UTC or as browser-local and shift it.
 */
export const formatWallClock = (value: string | null | undefined): string => {
    const match = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!match) return '';
    const [, yyyy, mm, dd, hh, mi] = match;
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

/** MMYY, as Pelecard's `CreditCardExpDate` carries it — for a receipt's Exp. Date box. */
export const toPelecardExpDate = (card: { expMonth?: number | null; expYear?: number | null }): string => {
    const month = Number(card?.expMonth);
    const year = Number(card?.expYear);
    if (!month || !year) return '';
    return `${String(month).padStart(2, '0')}${String(year % 100).padStart(2, '0')}`;
};
